<template>
  <div
    ref="buynow"
    class="pdp-buynow-container"
  >
    <div
      class="pdp-card-container"
      :class="borderTopClass"
    >
      <div
        v-if="showBannerDetails"
        class="d-none d-lg-flex"
      >
        <div class="pdp-banner pdp-text ml-auto" :class="`sumo-bg-${bannerBgColor}`">
          <DealBannerContent
            :banner-details="bannerDetails"
            :tooltip-id="`desktop-deal-banner-tooltip-${idSuffix}`"
          />
        </div>
      </div>
      <div
        class="pdp-buynow-card"
        :class="{ 'pdp-buynow-card-top-padding': !showBannerDetails }"
      >
        <div
          v-if="$options.LISTING_TYPES_WITH_LOGO.includes(deal.listing_type)"
          class="d-none d-lg-block mt-2 mb-3"
        >
          <ListingTypeLogo
            :public-name="deal.public_name"
            :listing-type="deal.listing_type"
            :tooltip-id="`desktop-listing-type-tooltip-${idSuffix}`"
          />
        </div>
        <h4 class="d-none d-lg-block pdp-section-title font-weight-bold col-12 p-0 h2">
          {{ deal.public_name }}
        </h4>
        <div class="d-none d-lg-flex mb-3">
          <div
            v-if="!deal.is_info"
            class="flex-grow-1 plan-type"
          >
            {{ planTypeText }}
          </div>
        </div>
        <p
          v-if="subheader && !deal.is_info"
          class="d-none d-lg-block pdp-product-subtitle p-0 col-12"
        >
          {{ subheader }}
        </p>
        <div
          v-if="!(deal.is_preview || deal.is_info) || isReportable"
          class="d-none d-lg-flex justify-content-between mb-3"
        >
          <TacoRating
            v-if="!(deal.is_preview || deal.is_info)"
            class=" m-0"
            :deal="deal"
          />
          <a
            v-if="isReportable"
            class="d-none d-lg-inline-block float-right"
            :href="reportUrl"
            target="_blank"
          >
            Report listing
            <font-awesome-icon
              icon="external-link-alt"
              height="16"
              width="16"
            />
          </a>
        </div>
        <div
          v-if="deal.is_info || inactiveIndefinitely"
          ref="soldOutEmailCapture"
          class="text-left"
        >
          <p v-if="deal.is_info" class="info-deal mb-1">
            <b class="font-weight-semi-bold">{{ infoDealQuestionText }} </b>
            Let us know — and make sure you never miss an amazing deal!
          </p>
          <p v-else-if="inactiveIndefinitely" class="indefinitely-inactive mb-1">
            <b class="font-weight-semi-bold">This deal is unavailable (a bummer, we know).</b>
            Don't miss a great deal again — drop your email to get all our latest tools!
          </p>
          <form
            v-if="!isPricingCardEmailSubmitted"
            @submit.prevent="postEmailWaitlistSignUp"
          >
            <label
              :for="emailInputId"
              class="sr-only"
            >
              Email
            </label>
            <input
              :id="emailInputId"
              v-model="emailAddress"
              type="email"
              name="email"
              placeholder="Email"
              class="form-control rounded mb-3"
              required
            >
            <button
              class="btn btn-primary indefinitely-inactive-btn"
              type="submit"
              :disabled="isProcessing || !isEmailAddressValid"
            >
              <slot name="button">
                {{ emailCaptureButtonText }}
              </slot>
            </button>
          </form>
          <div v-if="isPricingCardEmailSubmitted" class="success">
            <img src="~/static/success-tick.svg" alt="">
            <span class="pl-2">
              <b>You're in!</b>  Thanks for signing up.
            </span>
          </div>
        </div>
        <div v-else>
          <div
            v-if="hasPlans"
            class="pdp-buynow-dropdown mb-4"
          >
            <div class="d-flex justify-content-between mb-2">
              <span class="h5 mb-0 font-weight-bold">
                Select a plan
              </span>
              <span>
                <a
                  class="font-size-md font-weight-normal"
                  href="#pricePlans"
                > View plan details </a>
              </span>
            </div>
            <LegacyDropdown
              class="text-left pricing-card"
              :options="dropdownOptions"
              :value="dropdownValue"
              highlight
              @selected="selectPlan"
              @toggle="onTogglePlanDropdown"
            >
              <span :class="{'default-plan': !selectedPlan}">
                {{ dropdownText }}
              </span>
              <span v-if="showOriginalPricePricingCard" class="pdp-buynow-old-price">
                {{ selectedPlan.original_price | currencyWithOptionalDecimal }}
              </span>
            </LegacyDropdown>
          </div>
          <div
            v-else-if="deal.default_price"
            class="p-0 mx-0 mb-4 col-12 row d-flex"
          >
            <div class="d-flex w-100">
              <strong
                v-if="isOneTimePurchase"
                class="one-time-purchase"
              >
                One time purchase of
              </strong>
            </div>
            <div class="d-flex align-items-center">
              <div class="pdp-buynow-price pdp-h3">
                {{ defaultPriceText }}
              </div>
              <div
                v-if="deal.default_original_price"
                class="pdp-buynow-old-price ml-2"
              >
                {{ deal.default_original_price | currencyWithOptionalDecimal }}
              </div>
            </div>
          </div>
          <div
            v-if="earlyAccessOrLastCall"
            class="pdp-buynow-plus col-12 p-0"
          >
            <div class="d-block w-100 text-center d-lg-flex justify-content-between">
              <!-- jw: if the user does not have plus then link them to learn more about plus -->
              <a
                v-if="!user.hasPlus"
                href="/plus/"
                class="btn btn-primary pdp-buynow-plus d-inline-flex align-items-center
                  justify-content-center w-100 w-md-auto"
                aria-label="Join AppSumo Plus to buy now"
              >
                Join
                <img
                  class="ml-2"
                  :src="plusImage"
                  width="146"
                  height="14"
                  alt="AppSumo Plus"
                >
                <span class="ml-2">to buy now</span>
              </a>
              <!-- jw: if the user does have plus then allow them to buy like normal! -->
              <button
                v-else
                class="btn btn-primary pdp-buynow-btn d-inline-flex align-items-center
                  justify-content-center w-100 w-md-auto"
                :disabled="disabledBuyButton"
                aria-label="Buy now with AppSumo Plus"
                @click="postSignupOrAddToCart()"
              >
                Buy now with
                <img
                  class="ml-2"
                  :src="plusImage"
                  width="146"
                  height="14"
                  alt="AppSumo Plus"
                >
              </button>
            </div>
          </div>
          <div
            v-else
            class="d-flex flex-column flex-lg-row"
          >
            <!-- for deals with clickthrough url, use link instead -->
            <a
              v-if="clickthroughUrl"
              class="btn btn-primary pdp-buynow-btn"
              :href="clickthroughUrl"
            >
              {{ buyButtonText }}
            </a>
            <div v-else>
              <button
                class="btn btn-primary pdp-buynow-btn"
                :disabled="disabledBuyButton"
                @click="postSignupOrAddToCart()"
              >
                {{ buyButtonText }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Upsell Callout contianer -->
      <DealUpsellCallout
        v-if="deal.upsell_callout"
        :upsell-callout="deal.upsell_callout"
      />
      <PricingCardGuaranteeWrapper
        v-if="deal.money_back_callout_type"
        :callout-type="deal.money_back_callout_type"
        :refundable-days="deal.refundable_days"
        :is-freebie="deal.is_free"
      />
    </div>
    <PDPTimer
      v-if="displayTimer"
      class="col-12 mx-0 mt-4 row d-flex justify-content-center"
      :date="deal.dates.end_date"
    />
    <div
      v-if="isReportable"
      class="d-flex d-lg-none justify-content-center w-100 py-2"
    >
      <a
        class="pdp-buynow-report float-right"
        :href="reportUrl"
        target="_blank"
      >
        Report listing
        <font-awesome-icon
          icon="external-link-alt"
          height="16"
          width="16"
        />
      </a>
    </div>
    <!-- Mobile sticky info -->
    <transition name="fade">
      <div
        v-if="showStickyBuyNow && !isPricingCardEmailSubmitted"
        class="pdp-buynow-mobile-head d-lg-none d-flex align-items-center justify-content-around"
      >
        <div
          v-if="deal.default_price"
          class="px-2"
          :class="{ 'text-center': !inactiveIndefinitely }"
        >
          <div
            v-if="deal.is_info || inactiveIndefinitely"
          >
            <span
              v-if="deal.is_info"
              class="deal-unavailable-sticky"
            >
              <b>{{ infoDealQuestionText }} </b>
              Let us know!
            </span>
            <span
              v-else-if="inactiveIndefinitely"
              class="deal-unavailable-sticky"
            >
              <b>This deal is unavailable </b>
              — Sign up to get our latest deals!
            </span>
          </div>
          <div
            v-else
            class="pdp-buynow-price-sticky p-0 m-0"
          >
            <span v-if="selectedPlan">{{ selectedPriceText }}</span>
            <span v-else>{{ defaultPriceText }}</span>
            <span
              v-if="deal.default_original_price"
              class="pdp-buynow-old-price"
            >
              {{ deal.default_original_price | currencyWithOptionalDecimal }}
            </span>
          </div>
        </div>
        <!-- an: make button full width in case no default_price usign flex-grow -->
        <div
          class="px-2"
          :class="deal.default_price ? 'flex-shrink-0' : 'flex-grow-1'"
        >
          <DButton
            v-if="deal.is_info || inactiveIndefinitely"
            class="pdp-buynow-plus-mobile"
            @click="onClickEmailMeMobile"
          >
            {{ stickyFooterButtonText }}
          </DButton>
          <!-- if deal has clickthrough, use a link button to send them to that URL -->
          <a
            v-else-if="clickthroughUrl"
            class="btn btn-primary pdp-buynow-btn-mobile"
            :href="clickthroughUrl"
          >
            {{ buyButtonText }}
          </a>
          <!-- jw: if the user doesn't have plus link them to learn more about it. -->
          <a
            v-else-if="notPlusAndEarlyAccessOrLastCall"
            href="/plus/"
            class="btn btn-primary pdp-buynow-plus-mobile"
          >
            {{ buyButtonText }}
          </a>
          <!-- jw: if the user does have plus then allow them to buy it like normal. -->
          <button
            v-else
            class="btn btn-primary pdp-buynow-btn-mobile"
            :disabled="disabledBuyButton"
            @click="postSignupOrAddToCart()"
          >
            {{ buyButtonText }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import get from 'lodash/get';
import dayjs from 'dayjs';

// TODO:BRO-1276: replace DTacoRating, DDropdownItem, DButton where possible
import { DButton } from '@appsumo/dorado-button';
import {
  SUMO_DAY_BANNER_TEXT,
  COMING_SOON_BANNER_TEXT,
  OVERLAY_OVER_NAVBAR_CLASS,
} from '@/constants/global';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LegacyDropdown from '../ui/LegacyDropdown.vue';
import TacoRating from '../utilities/TacoRating.vue';
import PlusImage from '../../../static/images/home/as-plus-2.svg';
import PDPTimer from './util/PDPTimer.vue';
import DealUpsellCallout from './util/DealUpsellCallout.vue';
import SubmitEmailMixin from '../../mixins/submit-email';
import PDPScrollToElementMixin from './mixins/PDPScrollToElementMixin';
import PricingCardGuaranteeWrapper from './PricingCardGuaranteeWrapper.vue';
import ListingTypeLogo from './ListingTypeLogo.vue';
import { LISTING_TYPES_WITH_LOGO } from '~/constants/listing-types';
import DealBannerContent from './DealBannerContent.vue';
import { DEAL_STAGE } from '~/constants/deal-stages';

export default {
  LISTING_TYPES_WITH_LOGO,
  components: {
    LegacyDropdown,
    PDPTimer,
    TacoRating,
    DButton,
    PricingCardGuaranteeWrapper,
    DealUpsellCallout,
    ListingTypeLogo,
    DealBannerContent,
    FontAwesomeIcon,
  },
  mixins: [SubmitEmailMixin, PDPScrollToElementMixin],
  props: {
    deal: {
      type: Object,
      required: true,
    },
    buyButton: {
      type: Object,
      required: true,
    },
    planTypeText: {
      type: String,
      required: true,
    },
    subheader: {
      type: String,
      required: false,
      default: '',
    },
    plans: {
      type: Array,
      required: false,
      default: () => [],
    },
    // TODO temp user object to be replaced by this.$store.state.session.user
    user: {
      type: Object,
      required: true,
    },
    idSuffix: {
      type: String,
      required: true,
    },
    isPricingCardEmailSubmitted: {
      type: Boolean,
      required: true,
    },
    bannerDetails: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      plusImage: PlusImage,
      isMounted: false,
      isDisabled: false,
      tooltipText: `This is a short description of what AppSumo Select means.
                    We should cap this to only be a few lines in length.`,
      selectedPlan: this.plans?.length ? this.plans[0] : null,
      showStickyBuyNow: false,
      mobileMatchMedia: null,
      buyNowObserver: null,
      footerObserver: null,
      showOriginalPricePricingCard: false,
    };
  },
  async fetch() {
    this.showOriginalPricePricingCard = await this.$experiments.enroll('original-price-pricing-card') === 'enabled';
  },
  computed: {
    bannerBgColor() {
      return this.bannerDetails?.bg_color;
    },
    borderTopClass() {
      if (!this.bannerBgColor) {
        return null;
      }

      return `sumo-border-top-4-${this.bannerBgColor}`;
    },
    infoDealQuestionText() {
      return `Want to see ${this.deal.public_name} on AppSumo?`;
    },
    emailCaptureButtonText() {
      return this.deal.is_info ? 'Yes, bring to AppSumo' : 'Stay in the know';
    },
    stickyFooterButtonText() {
      return this.deal.is_info ? 'I\'d buy it' : 'Email me';
    },
    emailInputId() {
      return `email-${this.idSuffix}`;
    },
    buyButtonText() {
      if (this.buyButton.is_draft) {
        return 'Buy now (Draft)';
      }

      // is_coming_soon will be true if the current user does not have early access, so we want
      //     to handle these cases first since early access behavior should trump coming soon.
      //
      // note: buyButton.is_coming_soon behaves slightly differently than our bannerDetails
      // coming soon, so we're including the banner details check here to match buy button text
      // to our pdp stage banner as a temporary fix
      if (!this.earlyAccessOrLastCall
        && (this.bannerDetails?.text === COMING_SOON_BANNER_TEXT || this.buyButton.is_coming_soon)
      ) {
        return 'Coming soon';
      }

      if (this.buyButton.is_ended) {
        return 'Sold out';
      }

      if (this.buyButton.is_get_now) {
        return this.deal.is_free ? 'Get now for FREE' : 'Get now';
      }

      return 'Buy now';
    },
    clickthroughUrl() {
      return this.deal.clickthrough_url;
    },
    disabledBuyButton() {
      return this.disabledState || this.buyButton.is_coming_soon;
    },
    canAddToCart() {
      const { buyButton } = this;
      return !(buyButton.is_draft || buyButton.is_ended || buyButton.is_coming_soon);
    },
    defaultPriceText() {
      const formattedPrice = this
        .$options
        .filters
        .currencyWithOptionalDecimal(this.deal.default_price);

      if (this.deal.plan_stack_count > 1) {
        return `Starting at ${formattedPrice}`;
      }

      return formattedPrice;
    },
    selectedPriceText() {
      if (this.selectedPlan) {
        return this.getPlanDisplayText(this.selectedPlan);
      }

      return this.defaultPriceText;
    },
    isReportable() {
      // let's only allow reporting for self-listing/marketplace
      if (!this.deal.is_marketplace) {
        return false;
      }

      // info deals are 'marketplace', but we control them, so let's not allow reporting
      if (this.deal.is_info) {
        return false;
      }

      return this.user?.isAuthenticated || this.user?.isStaff;
    },
    reportUrl() {
      // jl: as this method's used right now, user is guaranteed to be non-empty.
      // while it won't error due to the presence of a default user value, for future uses
      // of reportUrl(), let's make sure the user object is non-empty
      return `https://report-offer.paperform.co/?deal_id=${this.deal.id}&deal_slug=
              ${this.deal.slug}&email=${this.user.email}`;
    },
    isAuthenticated() {
      return this.user.isAuthenticated;
    },
    signupText() {
      return this.isAuthenticated
        ? 'Don\'t ever relive this heartbreak— sign up below to get the latest deals!'
        : 'Don\'t ever relive this heartbreak— enter your email to get the latest deals!';
    },
    hasPlans() {
      return this.plans && this.plans.length > 1;
    },
    dropdownText() {
      // jw: since this is only called when we have multiple plans we should always have a
      //     selectedPlan option. Let's build this method with that assumption.
      return this.getPlanDisplayText(this.selectedPlan);
    },
    dropdownValue() {
      // jw: just like above, we should always have a selectedPlan since this is only called where
      //     hasPlans is true. See data.selectedPlan initial definition if you don't agree!
      return this.selectedPlan.price;
    },
    dropdownOptions() {
      return this.plans.map((plan) => ({
        id: plan.id,
        value: plan.price,
        display: `${this.getPlanDisplayText(plan)}`,
        subText: this.getOriginalPricePricingCard(plan.original_price),
      }));
    },
    disabledState() {
      if (this.buyButton.is_ended) {
        return true;
      }
      if (this.hasPlans) {
        return !this.selectedPlan;
      }
      return this.isDisabled;
    },
    isOriginals() {
      return (get(this, ['deal', 'products', 0, 'internal_product_tags'], []) || []).includes('originals');
    },
    isOneTimePurchase() {
      if (this.selectedPlan.plan_desc === 'One Time Purchase of') {
        return true;
      }

      // an: When only has one price
      return !this.hasPlans && !!this.deal.default_price;
    },
    stageDetails() {
      return this.deal.stage_details || {};
    },
    currentlyBeforeNextDealStage() {
      const nextStageStartDate = this.stageDetails.next_stage_start_date;
      if (!nextStageStartDate) {
        return false;
      }

      const dayjsNow = dayjs();
      const diffInMilliseconds = dayjs(nextStageStartDate).diff(dayjsNow);

      return diffInMilliseconds > 0;
    },
    lowCodesRemaining() {
      return this.deal.shows_codes_remaining && this.deal.codes_remaining > 0;
    },
    showBannerDetails() {
      if (!this.bannerDetails?.text) {
        return false;
      }

      return this.currentlyBeforeNextDealStage
          || this.lowCodesRemaining
          || this.bannerDetails?.text === SUMO_DAY_BANNER_TEXT;
    },
    displayTimer() {
      return this.deal.active_timer
          && this.stageDetails.current_stage !== DEAL_STAGE.INACTIVE
          && this.currentlyBeforeNextDealStage;
    },
    inactiveIndefinitely() {
      return this.stageDetails.is_inactive_indefinitely;
    },
    earlyAccessOrLastCall() {
      return this.buyButton.is_early_access || this.buyButton.is_last_call;
    },
    notPlusAndEarlyAccessOrLastCall() {
      return !this.user.hasPlus && this.earlyAccessOrLastCall;
    },
  },
  mounted() {
    this.isMounted = true;

    this.mobileMatchMedia = window.matchMedia('(max-width: 991px)');
    this.mobileMatchMedia.addListener(this.toggleObservers);

    this.toggleObservers(this.mobileMatchMedia);
  },
  beforeDestroy() {
    this.mobileMatchMedia.removeListener(this.toggleObservers);
    this.destroyObservers();
  },
  methods: {
    // seems silly, but let's centralize this common format into a helper function.
    getPlanDisplayText(plan) {
      const name = this.deal.use_licensing ? plan.public_name : this.pluralizeCodeText(plan.codes);

      return `${name} : ${this.$options.filters.currencyWithOptionalDecimal(plan.price)}`;
    },
    pluralizeCodeText(code) {
      return `${code} Code${code > 1 ? 's' : ''}`;
    },
    onClickEmailMeMobile() {
      const { soldOutEmailCapture } = this.$refs;
      this.$_scrollToElement(soldOutEmailCapture);
    },
    toggleObservers(event) {
      if (event.matches) {
        this.registerBuyNowObserver();
      } else {
        // remove any observer
        this.showStickyBuyNow = false;
        this.destroyObservers();
      }
    },
    registerBuyNowObserver() {
      this.buyNowObserver = new IntersectionObserver((entries) => {
        const [buyNowEntry] = entries;
        const isAfterViewport = buyNowEntry.boundingClientRect.bottom <= 0;

        if (!buyNowEntry.isIntersecting && isAfterViewport) {
          this.showStickyBuyNow = true;
          this.registerFooterObserver();
        } else if (this.footerObserver) {
          this.showStickyBuyNow = false;
          this.footerObserver.disconnect();
          this.footerObserver = null;
        }
      });

      this.buyNowObserver.observe(this.$refs.buynow);
    },
    registerFooterObserver() {
      const footer = document.querySelector('footer');

      this.footerObserver = new IntersectionObserver((entries) => {
        const [footerEntry] = entries;
        this.showStickyBuyNow = !footerEntry.isIntersecting;
      });

      this.footerObserver.observe(footer);
    },
    destroyObservers() {
      if (this.buyNowObserver) {
        this.buyNowObserver.disconnect();
        this.buyNowObserver = null;
      }

      if (this.footerObserver) {
        this.footerObserver.disconnect();
        this.footerObserver = null;
      }
    },
    selectPlan(plan) {
      this.selectedPlan = this.plans.find((p) => p.id === plan.id);
    },
    async postSignupOrAddToCart() {
      if (this.buyButton.is_ended) {
        await this.postEmailWaitlistSignUp();
      } else {
        this.postAddToCart();
      }
    },
    postAddToCart() {
      this.isDisabled = true;
      const data = new FormData();
      data.append('id', this.deal.id);
      if (this.selectedPlan) {
        data.append('plan_id', this.selectedPlan.id);
        data.append('quantity', this.selectedPlan.codes);
      } else if (this.deal.default_plan) {
        data.append('plan_id', this.deal.default_plan.id);
      }
      if (this.deal.is_free) {
        const quantity = (this.deal.use_licensing) ? 1 : this.deal.default_plan.codes;
        data.append('quantity', quantity);
      }
      axios.post('/cart/add/', data, this.$_formHeaders())
        .then(() => {
          this.isDisabled = false;
          if (window) {
            window.location = '/cart/';
          }
        })
        .catch(() => {
          this.$toastr.e('Something has gone wrong with this request. Please try again.');
          this.isDisabled = false;
        });
    },
    async postEmailWaitlistSignUp() {
      if (!this.$_validateEmail(this.emailAddress)) {
        this.$toastr.w('Please use a valid email address.');
      } else {
        const data = this.$_createFormDataWithUtm();
        data.append('deal_slug', this.deal.slug);
        data.append('waitlist_email', this.emailAddress);

        const showToastMessageOnSuccess = false;
        await this.$_sendEmailRequest('/deal/waitlist-signup/', data, showToastMessageOnSuccess);
      }
    },
    getOriginalPricePricingCard(originalPrice) {
      if (this.showOriginalPricePricingCard) {
        return `${this.$options.filters.currencyWithOptionalDecimal(originalPrice)}`;
      }
      return null;
    },
    onTogglePlanDropdown(isOpen) {
      document.body.classList.toggle(OVERLAY_OVER_NAVBAR_CLASS, isOpen);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~/styles/dorado/themes/dorado';
  @import '../../styles/abstracts/mixins';
  @import '../../styles/utils/breakpoints';

  /* stylelint-disable selector-no-qualifying-type */
  .info-deal,
  .indefinitely-inactive,
  .one-time-purchase {
    font-size: 14px;
    line-height: 21px;
  }

  .pdp-buynow-mobile-head {
    background: #fff;
    bottom: 0;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    left: 0;
    padding: 10px 5px;
    position: fixed;
    right: 0;
    z-index: 9000;
  }

  p.pdp-product-subtitle,
  h4.pdp-buynow-signup,
  input.pdp-buynow-signup,
  a.pdp-buynow-plus,
  button.pdp-buynow-btn,
  span.pdp-buynow-mobile-head,
  .pdp-buynow-plus-mobile,
  a.pdp-buynow-btn-mobile,
  button.pdp-buynow-btn-mobile {
    font-size: 16px;
    line-height: 24px;
  }

  h4.pdp-buynow-signup {
    font-weight: 600;
    color: #1b1b1b;
  }

  input.pdp-buynow-signup {
    padding: 8px 16px;
    border: 1px solid #434d59;
    border-radius: 4px;
  }

  a.pdp-buynow-plus,
  button.pdp-buynow-btn,
  .pdp-buynow-plus-mobile,
  a.pdp-buynow-btn-mobile,
  button.pdp-buynow-btn-mobile {
    font-weight: 700;
    padding: 8px 24px;
    border-radius: 40px;
  }

  // an: Limit buttons width when having default_price
  .flex-shrink-0 .pdp-buynow-plus-mobile,
  .flex-shrink-0 .pdp-buynow-btn-mobile {
    max-width: 195px;
    white-space: inherit;
  }

  // an: Make buttons full width when not having default_price
  .flex-grow-1 .pdp-buynow-plus-mobile,
  .flex-grow-1 .pdp-buynow-btn-mobile {
    width: 100%;
  }

  div.pdp-buynow-price {
    font-weight: 500;
  }

  /* purgecss ignore */
  .pdp-buynow-dropdown ::v-deep .subtext {
    text-decoration: line-through;
  }

  span.pdp-buynow-old-price {
    text-decoration: line-through;
    color: #8e9baa;
    font-size: 16px;
    line-height: 21px;
  }

  div.pdp-buynow-old-price {
    color: #434d59;
    font-size: 14px;
    font-weight: 400;
    text-decoration: line-through;
  }

  .deal-unavailable-sticky {
    font-size: 16px;
    line-height: 21px;
  }

  div.pdp-buynow-price-sticky {
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  }

  .plan-type {
    color: #434d59;
    font-weight: 700;
  }

  p.pdp-buynow-codes {
    font-size: 16px;
    color: #434d59;
  }

  a.pdp-buynow-viewplans,
  a.pdp-buynow-report {
    font-size: 16px;
    padding: 8px 0;
  }

  div.pdp-buynow-dropdown {
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }

  .default-plan {
    color: #c7ced7;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s ease;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  [data-popover='select'] {
    border-radius: 4px;
    color: #ffff;
    padding: 15px;
    font-size: 12px;
    width: 300px !important;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background-color: #09292b !important;
    margin-top: -6px;
  }

  .pdp-buynow-card {
    text-align: center;
  }

  @include media-breakpoint-down(md) {
    // due to nature of pricing card being overloaded, we need this temp hack to prevent
    // deal stage banner border from appearing on mobile
    div[class*='sumo-border-top-4-'] {
      border-top: 0 !important;
    }

    .pdp-buynow-btn {
      width: 100%;
    }
  }

  @media (min-width: 992px) {
    .pdp-card-container {
      @include pdp-card-style;
    }

    .pdp-banner {
      margin-right: 24px;
      border-radius: 0 0 4px 4px;
      padding: 4px 8px;
      font-weight: 500px;
    }

    .pdp-buynow-card-top-padding {
      padding-top: 28px;
    }

    div.pdp-buynow-card {
      padding-left: 28px;
      padding-right: 28px;
      padding-bottom: 28px;
      text-align: left;
    }
  }

  /* potential temp styling below */
  .btn-primary {
    border: 1px solid #ffbc00;
    background-color: #ffbc00;
    color: #272727;
    padding: 12px 32px;
    font-size: 16px;
  }

  .btn-primary:hover,
  .btn-primary:disabled {
    border-color: #ffcb33;
    background-color: #ffcb33;
    color: #272727;
  }

  .btn-primary:active,
  .btn-primary:focus {
    border-color: #ffcb33 !important;
    background-color: #ffcb33 !important;
    color: #272727 !important;
  }
  /* stylelint-enable selector-no-qualifying-type */

  .indefinitely-inactive-btn {
    border-radius: 40px;

    @include media-breakpoint-down(md) {
      width: 100%;
    }
  }

  .success {
    background: #0a840a;
    padding: 16px 20px;
    color: #fff;
    display: inline-flex;
    align-items: center;
  }

  .pdp-add-to-cart-btn {
    margin: 1rem 0 0;

    // Having to include this rule instead of the standard media breakpoint
    //  because of the above definition...
    @media (min-width: 992px) {
      margin: 0 0 0 1rem;
    }
  }
</style>
